import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import { useDailyPlanRoles } from 'features/daily-plan/hooks';
import { useUserContext } from 'providers/UserProvider';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { getInitialTeamFilters } from 'UI/pages/DailyPlan/DailyPlan.utils';
import { REQUIRED_VALIDATION } from 'UI/utils';

import { getDefaultRecruiterOption } from '../utils';

import { FORM_FIELDS_MAP, TEAM_ENDPOINTS } from './TeamSelectors.constants';

export const TeamSelectors = () => {
  const [currentUser] = useUserContext();
  const { register, unregister, setValue, watch, errors } = useFormContext();

  const formValues = watch();
  const selectedTeam = formValues[FORM_FIELDS_MAP.Team.key];
  const selectedTeamId = selectedTeam?.id;
  const coachId = currentUser?.teamRelation?.coachId;

  const {
    isDirector,
    isAccountability,
    isOperations,
    shouldShowTeamSelector,
    shouldShowRecruiterSelector
  } = useDailyPlanRoles();

  const defaultRecruiterOption = useMemo(
    () => getDefaultRecruiterOption({ currentUser }),
    [currentUser]
  );

  const recruiterPrependOptions = useMemo(
    () => (defaultRecruiterOption ? [defaultRecruiterOption] : []),
    [defaultRecruiterOption]
  );

  useEffect(() => {
    if (shouldShowTeamSelector) {
      register(FORM_FIELDS_MAP.Team.key, { ...REQUIRED_VALIDATION });
    }
    if (shouldShowRecruiterSelector) {
      register(FORM_FIELDS_MAP.Recruiter.key, { ...REQUIRED_VALIDATION });
    }

    return () => {
      if (shouldShowTeamSelector) {
        unregister(FORM_FIELDS_MAP.Team.key);
      }
      if (shouldShowRecruiterSelector) {
        unregister(FORM_FIELDS_MAP.Recruiter.key);
      }
    };
  }, [shouldShowTeamSelector, shouldShowRecruiterSelector, register, unregister]);

  useEffect(() => {
    if (!shouldShowTeamSelector) return;
    const { coach } = getInitialTeamFilters(currentUser);
    setValue(FORM_FIELDS_MAP.Team.key, coach?.value ?? null);
  }, [currentUser, setValue, shouldShowTeamSelector]);

  useEffect(() => {
    if (!shouldShowRecruiterSelector) return;
    const { recruiter } = getInitialTeamFilters(currentUser);
    setValue(FORM_FIELDS_MAP.Recruiter.key, recruiter?.value ?? null);
  }, [currentUser, setValue, shouldShowRecruiterSelector]);

  const handleTeamSelect = (name, value) => {
    setValue(FORM_FIELDS_MAP.Team.key, value);
    setValue(FORM_FIELDS_MAP.Recruiter.key, null);
  };

  const handleRecruiterSelect = (name, value) => {
    setValue(FORM_FIELDS_MAP.Recruiter.key, value);
  };

  const recruiterUrl = useMemo(() => {
    if (!shouldShowRecruiterSelector) return null;

    if (isAccountability || isOperations) {
      return selectedTeamId ? TEAM_ENDPOINTS.recruitersByCoach(selectedTeamId) : null;
    }

    if (isDirector && coachId) {
      return TEAM_ENDPOINTS.recruitersByCoach(coachId);
    }

    return null;
  }, [
    coachId,
    isAccountability,
    isDirector,
    isOperations,
    selectedTeamId,
    shouldShowRecruiterSelector
  ]);

  return (
    <Grid container>
      <When condition={shouldShowTeamSelector}>
        <Grid item xs={12}>
          <AutocompleteSelect
            name={FORM_FIELDS_MAP.Team.key}
            placeholder={strings.dailyPlan.filters.leadership.teams}
            selectedValue={selectedTeam}
            onSelect={handleTeamSelect}
            url={TEAM_ENDPOINTS.coaches}
            displayKey="full_name"
            error={!!errors[FORM_FIELDS_MAP.Team.key]}
            errorText={errors[FORM_FIELDS_MAP.Team.key]?.message}
          />
        </Grid>
      </When>

      <When condition={shouldShowRecruiterSelector}>
        <Grid item xs={12}>
          <AutocompleteSelect
            name={FORM_FIELDS_MAP.Recruiter.key}
            placeholder={strings.dailyPlan.filters.leadership.selector.recruiters}
            selectedValue={formValues[FORM_FIELDS_MAP.Recruiter.key]}
            onSelect={handleRecruiterSelect}
            url={recruiterUrl}
            displayKey="full_name"
            disabled={shouldShowTeamSelector && !selectedTeam}
            error={!!errors[FORM_FIELDS_MAP.Recruiter.key]}
            errorText={errors[FORM_FIELDS_MAP.Recruiter.key]?.message}
            prependOptions={recruiterPrependOptions}
          />
        </Grid>
      </When>
    </Grid>
  );
};
